import React from "react"
import { usePageContext } from "vike-react/usePageContext"
import Typography from "aldoo-ra/Typography"
import CategoryBreadcrumb from "../ui/Categories/CategoryBreadcrumb"
import { CategoryColumn, ContentTypeColumn } from "../ui/Categories/CategoryColumn"
import CategoryFilterSheet from "../ui/Categories/CategoryFilterSheet"
import CategoryItemBrowser from "../ui/CategoryItemBrowser"
import CategoryLatestRows from "../ui/Categories/CategoryLatestRows"
import TermMatches from "../ui/Categories/TermMatches"
import AdPlacement from "../ui/Promotions/AdPlacement"
import SeriesBanner from "../ui/SeriesBanner"
import useT from "../hooks/useT"
import useSearchParams from "../hooks/useSearchParams"
import { useCategories } from "../hooks/useCatalogData"
import { usePageTitle } from "../hooks/usePageTitle"
import { stripLang } from "../utils/contentRoutes"
import { collectCategoryIds, findCategory } from "../utils/categories"
import { categoryOnBeforeRender } from "./catalogSSR"
import { useCategoryTrail, useRememberTrail } from "../hooks/useCategoryTrail"
import { trackSearch } from "../tracking"

/**
 * /books (all books / text search results) and /category/<path...>
 * (last segment is the active category; its whole subtree is listed).
 */
export default function CategoryPage() {
  const t = useT()
  const pageContext = usePageContext()
  const params = useSearchParams()
  const path = stripLang(pageContext.urlPathname || "/")
  const segments = path.split("/").filter(Boolean)
  const category = segments[0] === "category" ? segments[segments.length - 1] : undefined
  const textSearch = params.get("textSearch") || ""
  // a search run (GA4 search): once per term shown
  React.useEffect(() => {
    if (textSearch) trackSearch(textSearch)
  }, [textSearch])
  const { list } = useCategories()
  // the way the visitor came down: a product opened from here is shown under it
  useRememberTrail(useCategoryTrail())

  const categoryObj = findCategory(list, category)
  // Server-resolved name keeps the SSR h1/title right before the categories load client-side
  const categoryName = categoryObj?.name ?? (category ? pageContext.storefrontCategoryName : undefined)
  const search = textSearch.length > 0 ? t('containing "%search-text%"', textSearch) : ""
  // no category and no search: the rows of the newest of each category (CategoryLatestRows) — said so in the title
  const allTitle = textSearch ? t`All Products` : t`Latest`
  const title = [categoryName ?? allTitle, search].filter(Boolean).join(categoryName && search ? ", " : " ")
  usePageTitle(title)

  const categoryIds = category ? (list.length ? collectCategoryIds(category, list) : [category]) : []

  return (
    <div className="w-full flex flex-col items-center mb-24">
      {category && (
        <div className="w-[90%] max-w-w1_xl mt-6">
          <CategoryBreadcrumb categoryId={category} />
        </div>
      )}

      <div className="w-[90%] max-w-w1_xl flex gap-6 mt-6">
        <aside className="hidden md:block w-[250px] shrink-0">
          <CategoryColumn parentCategoryId={category} />
          <ContentTypeColumn />
        </aside>

        <div className="flex-1 min-w-0">
          {/* a phone reads the name in the breadcrumb and on the filter button:
              the heading is the desktop's */}
          <Typography component="h1" variant="h4" className="hidden md:block leading-none mb-2">
            {title}
          </Typography>
          {category && <AdPlacement placement={`${category}-promos`} className="mt-6 mb-8" />}
          {/* a series with more than one book: the whole series in one click (the books not in the cart yet) */}
          {category && !textSearch && <SeriesBanner categoryId={category} className="mb-6" />}
          {/* the side column lists the categories; a phone has no room for
              it, so a button opens the same lists as a sheet */}
          <div className="mb-6 md:hidden">
            <CategoryFilterSheet parentCategoryId={category} />
          </div>
          {/* a search also finds the categories and series named like it */}
          {textSearch && <TermMatches text={textSearch} className="mb-6" />}
          {/* no category and no search: the newest of each category, a row each */}
          {!category && !textSearch ? (
            <CategoryLatestRows />
          ) : (
            <CategoryItemBrowser
              key={`${category || "all"}:${textSearch}`}
              categories={categoryIds}
              textSearch={textSearch}
              // the products the server put in the page (catalogSSR) are this category's
              seedFor={category || null}
              // a search's results are a placement of their own
              placement={textSearch ? "search" : "category"}
            />
          )}
        </div>
      </div>
    </div>
  )
}

CategoryPage.onBeforeRender = categoryOnBeforeRender
